import React, { ElementType, PropsWithChildren } from "react";
import {
  Box,
  Button,
  Collapse,
  Flex,
  Stack,
  useColorModeValue,
} from "@chakra-ui/react";
import { FaEye } from "@react-icons/all-files/fa/FaEye";
import { FaGlasses } from "@react-icons/all-files/fa/FaGlasses";
import { FiAlertTriangle } from "@react-icons/all-files/fi/FiAlertTriangle";
import { FiChevronDown } from "@react-icons/all-files/fi/FiChevronDown";
import { FiChevronUp } from "@react-icons/all-files/fi/FiChevronUp";
import { MarkdownRenderer } from "./MarkdownRenderer";

import { useToggle } from "react-use";

type SectionConfig = {
  icon: ElementType;
  title: (title?: string) => string;
  colorScheme?: string;
};

const SECTION_CONFIGS = {
  nerd: {
    icon: FaGlasses,
    title: (title) => (title ? `Nerding out about: ${title}` : "Nerd alert!"),
    colorScheme: "yellow",
  } as SectionConfig,
  info: {
    icon: FaEye,
    title: (title) => (title ? `Going deeper: ${title}` : "Deep-dive"),
    colorScheme: "gray",
  } as SectionConfig,
  warning: {
    icon: FiAlertTriangle,
    title: (title) => (title ? `${title}` : "Deep-dive"),
    colorScheme: "red",
  } as SectionConfig,
};

type CollapsibleSectionProps = PropsWithChildren<{
  type?: keyof typeof SECTION_CONFIGS;
  title?: string;
  icon?: ElementType;
  colorScheme?: string;
}>;

export function useButtonColor(color: string) {
  const isGray = color === "gray";
  return useColorModeValue(
    `${color}.${isGray ? 100 : 500}`,
    `${isGray ? "whiteAlpha" : color}.200`
  );
}

export function CollapsibleSection({
  type,
  children,
  ...props
}: CollapsibleSectionProps) {
  const [isOpen, toggleOpen] = useToggle(false);
  const {
    title = "advanced",
    icon,
    colorScheme = "gray",
  } = type ? SECTION_CONFIGS[type] : props;
  const borderColor = useButtonColor(colorScheme);
  return (
    <>
      <Button
        fontSize="lg"
        onClick={() => {
          toggleOpen();
          window.gtag?.(
            "event",
            isOpen ? "collapse_deep_dive" : "expand_deep_dive",
            {
              title,
            }
          );
        }}
        width={"100%"}
        justifyContent="space-between"
        borderBottomRadius={isOpen ? "none" : "md"}
        mb={isOpen ? 0 : "32px"}
        mt="2"
        variant={"outline"}
        py="24px"
        colorScheme={colorScheme}
      >
        <Flex
          align="center"
          sx={{
            code: {
              mx: 1,
            },
            p: {
              whiteSpace: "pre-wrap",
              textAlign: "left",
            },
          }}
        >
          {icon && <Box mr="2" as={icon} />}
          <MarkdownRenderer>
            {typeof title === "function" ? title(props.title) : title}
          </MarkdownRenderer>
        </Flex>
        {isOpen ? <FiChevronUp /> : <FiChevronDown />}
      </Button>
      <Collapse in={isOpen} animateOpacity>
        <Stack
          p="4"
          spacing="4"
          borderWidth="1px"
          borderColor={borderColor}
          borderBottomRadius="md"
          borderTop="none"
          mb={isOpen ? 4 : 0}
        >
          {children}
        </Stack>
      </Collapse>
    </>
  );
}
